import { buttonHierarchyOptions } from 'molecules/Button/styles';
import { buttonIds } from 'molecules/Icon/types';

import type { Input } from '@builder.io/sdk';

const buttonIconEnum = ['No Icon', ...buttonIds] as const;

export const simpleButtonFields = [
  {
    name: 'label',
    type: 'string',
    defaultValue: 'Button CTA',
    helperText: 'Text displayed inside the button.',
  },
  {
    name: 'href',
    friendlyName: 'URL',
    type: 'string',
    defaultValue: 'https://www.prismatic.io/',
    helperText: 'Destination link. Use relative paths for internal pages (e.g., "/pricing").',
  },
  {
    name: 'mobileHref',
    friendlyName: 'Mobile URL (optional)',
    type: 'string',
    helperText:
      'Optional. If set, visitors on screens under 640px will be sent here instead of the main URL. Use this when the main URL is an anchor (#section) pointing to a block hidden on mobile.',
  },
] as const satisfies DeepReadonly<Input[]>;

export const buttonFields = [
  ...simpleButtonFields,
  {
    name: 'hierarchy',
    type: 'string',
    enum: buttonHierarchyOptions, // ['primary','secondary','tertiary','linkGray','linkColor','linkLight','custom']
    defaultValue: 'primary',
    helperText: 'Visual style of the button (primary, secondary, tertiary, or link variants).',
  },
  {
    name: 'startIcon',
    type: 'string',
    enum: buttonIconEnum,
    helperText: 'Optional icon displayed before the button label.',
  },
  {
    name: 'endIcon',
    type: 'string',
    enum: buttonIconEnum,
    helperText: 'Optional icon displayed after the button label.',
  },
  // Custom color controls
  {
    name: 'useCustomColors',
    type: 'boolean',
    defaultValue: false,
    helperText: 'Enable to override default button colors while keeping spacing and focus styles.',
  },
  {
    name: 'useGradient',
    type: 'boolean',
    defaultValue: true,
    showIf: 'options.get("useCustomColors") === true',
    helperText: 'If enabled, the button background will use gradient colors instead of solid colors.',
  },
  // Solid mode
  {
    name: 'bgColor',
    type: 'color',
    defaultValue: '#0ea5a5',
    showIf: 'options.get("useCustomColors") === true && options.get("useGradient") !== true',
    helperText: 'Background color of the button in normal state (solid mode).',
  },
  {
    name: 'hoverBgColor',
    type: 'color',
    defaultValue: '#14b8b8',
    showIf: 'options.get("useCustomColors") === true && options.get("useGradient") !== true',
    helperText: 'Background color of the button on hover (solid mode).',
  },
  // Gradient mode
  {
    name: 'gradientFrom',
    type: 'color',
    defaultValue: '#8CE1E4',
    showIf: 'options.get("useCustomColors") === true && options.get("useGradient") === true',
    helperText: 'Starting gradient color in normal state.',
  },
  {
    name: 'gradientTo',
    type: 'color',
    defaultValue: '#65D5DA',
    showIf: 'options.get("useCustomColors") === true && options.get("useGradient") === true',
    helperText: 'Ending gradient color in normal state.',
  },
  {
    name: 'hoverGradientFrom',
    type: 'color',
    defaultValue: '#9AE7E9',
    showIf: 'options.get("useCustomColors") === true && options.get("useGradient") === true',
    helperText: 'Starting gradient color on hover.',
  },
  {
    name: 'hoverGradientTo',
    type: 'color',
    defaultValue: '#73DBE0',
    showIf: 'options.get("useCustomColors") === true && options.get("useGradient") === true',
    helperText: 'Ending gradient color on hover.',
  },
  // Shared (text/border/focus)
  {
    name: 'textColor',
    type: 'color',
    defaultValue: '#101828',
    showIf: 'options.get("useCustomColors") === true',
    helperText: 'Color of the button text (applies in both solid and gradient modes).',
  },
  {
    name: 'borderColor',
    type: 'color',
    defaultValue: 'transparent',
    showIf: 'options.get("useCustomColors") === true',
    helperText: 'Border color of the button.',
  },
  {
    name: 'focusRingColor',
    type: 'color',
    defaultValue: '#22d3ee',
    helperText: 'Color of the focus outline shown for accessibility when the button is focused.',
    showIf: 'options.get("useCustomColors") === true',
  },
  {
    name: 'className',
    type: 'string',
    defaultValue: '',
    helperText: 'Optional class name for custom styling',
  },
] as const satisfies DeepReadonly<Input[]>;

export type SimpleButtonConfigProps = GenerateBuilder<typeof simpleButtonFields>;
export type ButtonConfigProps = GenerateBuilder<typeof buttonFields>;
